/**
 * Codes-barres EAN-13 à usage INTERNE.
 *
 * ⭐ Les préfixes 200 à 299 sont réservés par GS1 aux codes propres à un point
 * de vente : ils ne peuvent entrer en collision avec aucun code fabricant, et
 * restent lisibles par toutes les douchettes. Un EAN-13 inventé hors de cette
 * plage risquerait de désigner le produit de quelqu'un d'autre.
 *
 * ⚠️ Partagé entre le navigateur et le serveur : la saisie manuelle, l'import
 * et le rattrapage doivent produire des codes de même forme. Trois
 * implémentations auraient fini par diverger sur la clé de contrôle, et le
 * défaut ne se serait vu qu'à la première douchette.
 */
/** Clé de contrôle EAN-13 : somme pondérée 1/3 des douze premiers chiffres. */
export function cleEan13(douzeChiffres) {
    let somme = 0;
    for (let i = 0; i < 12; i++)
        somme += Number(douzeChiffres[i]) * (i % 2 === 0 ? 1 : 3);
    return (10 - (somme % 10)) % 10;
}
/**
 * Engendre un EAN-13 interne.
 *
 * ⚠️ Le noyau est ALÉATOIRE, non horodaté. La version précédente utilisait
 * `Date.now()` : deux articles créés dans la même milliseconde recevaient le
 * même code. Invisible à la saisie manuelle, désastreux pour un import de
 * quatre cents lignes, qui les aurait tous rendus identiques.
 */
export function genererCodeBarresEan13() {
    const noyau = Math.floor(Math.random() * 1_000_000_000);
    const base = `200${String(noyau).padStart(9, '0')}`;
    return `${base}${cleEan13(base)}`;
}
/**
 * Engendre un code ABSENT d'un ensemble donné.
 *
 * Sert aux traitements en masse — import, rattrapage — où la probabilité de
 * collision, négligeable pour un code, cesse de l'être sur des centaines.
 * `deja` doit contenir les codes existants ET ceux engendrés dans le même lot.
 */
export function genererCodeBarresUnique(deja) {
    for (let essai = 0; essai < 50; essai++) {
        const code = genererCodeBarresEan13();
        if (!deja.has(code)) {
            deja.add(code);
            return code;
        }
    }
    // 50 tirages tous en collision sur un espace d'un milliard signale une
    // anomalie, pas de la malchance : mieux vaut échouer franchement.
    throw new Error("Impossible d'engendrer un code-barres unique");
}
/** Un code est-il un EAN-13 complet et cohérent avec sa clé ? */
export function ean13Valide(code) {
    if (!/^\d{13}$/.test(code))
        return false;
    return cleEan13(code.slice(0, 12)) === Number(code[12]);
}
// ---------------------------------------------------- documents commerciaux
/**
 * Un numéro de facture n'est pas un nombre : `IN-2026-0008` porte deux lettres
 * et deux tirets, que l'EAN-13 ne sait pas coder.
 *
 * ⭐ On le TRADUIT en un EAN-13 interne, réversible. La boutique n'a qu'un seul
 * type de lecteur — celui qui scanne les articles en rayon — et lui présenter
 * une symbologie différente sur les tickets aurait obligé à en acheter un
 * second, ou à ressaisir la référence à la main.
 *
 * Composition des douze chiffres, puis la clé :
 *
 * ```
 *   29  1   26   0000008
 *   │   │   │    └── séquence du document dans l'année
 *   │   │   └─────── année sur deux chiffres
 *   │   └─────────── type : devis, facture, acompte, avoir, commande
 *   └─────────────── 29 : plage interne, distincte du 200 des ARTICLES.
 * ```
 *
 * ⚠️ Le préfixe ne recouvre pas celui des articles (`200…`), et c'est délibéré :
 * scanner un ticket de caisse ne doit jamais pouvoir ajouter un produit au
 * panier. Les deux plages restent dans le 20–29 réservé par GS1 à l'usage
 * interne, donc sans collision possible avec un code fabricant.
 */
/**
 * Préfixe littéral d'un numéro de document, en FRANÇAIS.
 *
 * ⚠️ La MÊME table qu'en base (`prefixe_document`, migration 0016). Le préfixe
 * s'y déduisait des deux premières lettres du type anglais — `invoice` → « IN »,
 * `quote` → « QU » — et deux types finissaient par se disputer les mêmes
 * lettres : `quote` et `deposit_invoice` réclamaient tous deux « DE ». La
 * correspondance est désormais explicite des deux côtés.
 */
export const PREFIXES_DOCUMENT = {
    invoice: 'FA',
    quote: 'DE',
    deposit_invoice: 'AC',
    credit_note: 'AV',
    order: 'CO',
};
const TYPE_VERS_CHIFFRE = {
    quote: '0',
    invoice: '1',
    deposit_invoice: '2',
    credit_note: '3',
    order: '4',
};
const CHIFFRE_VERS_TYPE = Object.fromEntries(Object.entries(TYPE_VERS_CHIFFRE).map(([type, chiffre]) => [chiffre, type]));
/** `IN-2026-0008` → `2912600000083`. `null` si le numéro n'est pas un numéro légal. */
export function eanDeDocument(docType, numero) {
    const chiffreType = TYPE_VERS_CHIFFRE[docType];
    if (!chiffreType || !numero)
        return null;
    /**
     * ⚠️ Une référence PROVISOIRE (`L-2026-A3F7-0042`) n'a pas sa place ici :
     * elle changera au retour du réseau, et le ticket déjà remis au client
     * porterait un code qui ne mène plus à rien.
     */
    const parties = /^[A-Z]+-(\d{4})-(\d+)$/.exec(numero);
    if (!parties)
        return null;
    const annee = Number(parties[1]);
    const sequence = Number(parties[2]);
    // Au-delà, il faudrait plus de douze chiffres : mieux vaut aucun code-barres
    // qu'un code qui désignerait un autre document.
    if (annee < 2000 || annee > 2099 || sequence > 9_999_999)
        return null;
    const douze = `29${chiffreType}${String(annee % 100).padStart(2, '0')}${String(sequence).padStart(7, '0')}`;
    return `${douze}${cleEan13(douze)}`;
}
/**
 * L'opération inverse : `2912600000083` → `DE-2026-0008`.
 *
 * ⭐ C'est elle qui rend le code-barres UTILE. Sans elle, on imprimerait un
 * dessin : scanner le ticket d'un client qui revient doit retrouver sa vente,
 * et une douchette ne sait taper qu'une suite de chiffres.
 *
 * ⚠️ Rien n'est stocké nulle part. Une colonne `ean` en base aurait été une
 * seconde vérité à tenir à jour, et fausse le jour où la première change.
 */
export function referencesDepuisEan(code) {
    if (!ean13Valide(code) || !code.startsWith('29'))
        return [];
    const docType = CHIFFRE_VERS_TYPE[code[2]];
    if (!docType)
        return [];
    const annee = 2000 + Number(code.slice(3, 5));
    const suite = `-${annee}-${String(Number(code.slice(5, 12))).padStart(4, '0')}`;
    /**
     * ⚠️ DEUX références, pas une : la courante et l'HISTORIQUE.
     *
     * Les préfixes sont passés de l'anglais au français en cours de route. Les
     * documents émis avant portent « IN-2026-0035 », ceux d'après
     * « FA-2026-0035 » — et le code-barres, lui, ne dit que le type et le rang.
     * Ne reconstruire que la forme courante rendrait introuvables tous les
     * tickets déjà imprimés : le client revient avec le sien, on le scanne, et
     * l'écran répond « aucun résultat ».
     */
    const courante = `${PREFIXES_DOCUMENT[docType] ?? docType.slice(0, 2).toUpperCase()}${suite}`;
    const historique = `${docType.slice(0, 2).toUpperCase()}${suite}`;
    return courante === historique ? [courante] : [courante, historique];
}
//# sourceMappingURL=ean13.js.map